import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import { Banner, PageLoading, Toggle } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type { CompanySettings, MobileMenuItem, MobileMenuKey } from '../../types';

// Countries with their ISO code, name, default currency and calling code.
// The admin picks a country; currency and calling code follow automatically.
const COUNTRIES: { iso: string; name: string; dial: string; currency: string }[] = [
  { iso: 'IN', name: 'India', dial: '+91', currency: 'INR' },
  { iso: 'US', name: 'United States', dial: '+1', currency: 'USD' },
  { iso: 'GB', name: 'United Kingdom', dial: '+44', currency: 'GBP' },
  { iso: 'AE', name: 'UAE', dial: '+971', currency: 'AED' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '+966', currency: 'SAR' },
  { iso: 'SG', name: 'Singapore', dial: '+65', currency: 'SGD' },
  { iso: 'AU', name: 'Australia', dial: '+61', currency: 'AUD' },
  { iso: 'CA', name: 'Canada', dial: '+1', currency: 'CAD' },
  { iso: 'DE', name: 'Germany', dial: '+49', currency: 'EUR' },
  { iso: 'FR', name: 'France', dial: '+33', currency: 'EUR' },
  { iso: 'IT', name: 'Italy', dial: '+39', currency: 'EUR' },
  { iso: 'ES', name: 'Spain', dial: '+34', currency: 'EUR' },
  { iso: 'NL', name: 'Netherlands', dial: '+31', currency: 'EUR' },
  { iso: 'BR', name: 'Brazil', dial: '+55', currency: 'BRL' },
  { iso: 'MX', name: 'Mexico', dial: '+52', currency: 'MXN' },
  { iso: 'JP', name: 'Japan', dial: '+81', currency: 'JPY' },
  { iso: 'KR', name: 'South Korea', dial: '+82', currency: 'KRW' },
  { iso: 'CN', name: 'China', dial: '+86', currency: 'CNY' },
  { iso: 'HK', name: 'Hong Kong', dial: '+852', currency: 'HKD' },
  { iso: 'ID', name: 'Indonesia', dial: '+62', currency: 'IDR' },
  { iso: 'MY', name: 'Malaysia', dial: '+60', currency: 'MYR' },
  { iso: 'TH', name: 'Thailand', dial: '+66', currency: 'THB' },
  { iso: 'PH', name: 'Philippines', dial: '+63', currency: 'PHP' },
  { iso: 'VN', name: 'Vietnam', dial: '+84', currency: 'VND' },
  { iso: 'BD', name: 'Bangladesh', dial: '+880', currency: 'BDT' },
  { iso: 'PK', name: 'Pakistan', dial: '+92', currency: 'PKR' },
  { iso: 'LK', name: 'Sri Lanka', dial: '+94', currency: 'LKR' },
  { iso: 'NP', name: 'Nepal', dial: '+977', currency: 'NPR' },
  { iso: 'ZA', name: 'South Africa', dial: '+27', currency: 'ZAR' },
  { iso: 'NG', name: 'Nigeria', dial: '+234', currency: 'NGN' },
  { iso: 'KE', name: 'Kenya', dial: '+254', currency: 'KES' },
  { iso: 'GH', name: 'Ghana', dial: '+233', currency: 'GHS' },
  { iso: 'EG', name: 'Egypt', dial: '+20', currency: 'EGP' },
  { iso: 'TR', name: 'Turkey', dial: '+90', currency: 'TRY' },
  { iso: 'RU', name: 'Russia', dial: '+7', currency: 'RUB' },
  { iso: 'PL', name: 'Poland', dial: '+48', currency: 'PLN' },
  { iso: 'SE', name: 'Sweden', dial: '+46', currency: 'SEK' },
  { iso: 'NO', name: 'Norway', dial: '+47', currency: 'NOK' },
  { iso: 'DK', name: 'Denmark', dial: '+45', currency: 'DKK' },
  { iso: 'FI', name: 'Finland', dial: '+358', currency: 'EUR' },
  { iso: 'CH', name: 'Switzerland', dial: '+41', currency: 'CHF' },
  { iso: 'AT', name: 'Austria', dial: '+43', currency: 'EUR' },
  { iso: 'BE', name: 'Belgium', dial: '+32', currency: 'EUR' },
  { iso: 'PT', name: 'Portugal', dial: '+351', currency: 'EUR' },
  { iso: 'IE', name: 'Ireland', dial: '+353', currency: 'EUR' },
  { iso: 'NZ', name: 'New Zealand', dial: '+64', currency: 'NZD' },
  { iso: 'IL', name: 'Israel', dial: '+972', currency: 'ILS' },
  { iso: 'QA', name: 'Qatar', dial: '+974', currency: 'QAR' },
  { iso: 'KW', name: 'Kuwait', dial: '+965', currency: 'KWD' },
  { iso: 'BH', name: 'Bahrain', dial: '+973', currency: 'BHD' },
  { iso: 'OM', name: 'Oman', dial: '+968', currency: 'OMR' },
  { iso: 'JO', name: 'Jordan', dial: '+962', currency: 'JOD' },
  { iso: 'LB', name: 'Lebanon', dial: '+961', currency: 'LBP' },
  { iso: 'AR', name: 'Argentina', dial: '+54', currency: 'ARS' },
  { iso: 'CL', name: 'Chile', dial: '+56', currency: 'CLP' },
  { iso: 'CO', name: 'Colombia', dial: '+57', currency: 'COP' },
  { iso: 'PE', name: 'Peru', dial: '+51', currency: 'PEN' },
  { iso: 'TZ', name: 'Tanzania', dial: '+255', currency: 'TZS' },
  { iso: 'UG', name: 'Uganda', dial: '+256', currency: 'UGX' },
  { iso: 'ET', name: 'Ethiopia', dial: '+251', currency: 'ETB' },
  { iso: 'MM', name: 'Myanmar', dial: '+95', currency: 'MMK' },
];

export default function SettingsPage() {
  const { can, company, refresh } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState('');
  const [editingCountry, setEditingCountry] = useState(false);
  const [countryVal, setCountryVal] = useState('');

  const load = useCallback(async () => {
    try {
      setSettings(await api.get<CompanySettings>('/settings'));
    } catch (err) {
      setLoadError(err instanceof ApiClientError ? err.message : 'Could not load settings.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /**
   * `optimistic` moves the control before the round trip and puts it
   * back if the save fails. A toggle that does not move until the server
   * answers reads as broken on a slow connection, and the admin taps it
   * again.
   *
   * The mode switch opts out: it is behind a confirmation, it changes
   * two other settings as a consequence, and showing it as done before
   * it is would be a lie about something consequential.
   */
  async function save(
    patch: Partial<CompanySettings>,
    success: string,
    { optimistic = true } = {},
  ) {
    const previous = settings;
    if (optimistic && previous) setSettings({ ...previous, ...patch });

    setBusy(true);
    try {
      const next = await api.patch<CompanySettings>('/settings', patch);
      setSettings(next);
      toast.success(success);

      // Some choices cannot be turned off in the mode that was chosen.
      // Saying so beats the admin finding a toggle moved on its own.
      for (const change of next.alsoChanged ?? []) {
        toast.toast(change.why);
      }
    } catch (err) {
      if (optimistic && previous) setSettings(previous);
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  async function saveName() {
    const trimmed = nameVal.trim();
    if (!trimmed || trimmed.length < 2) { toast.error('Give the company a name'); return; }
    setBusy(true);
    try {
      await api.patch('/settings/name', { name: trimmed });
      toast.success('Company renamed.');
      setEditingName(false);
      void refresh();           // updates the sidebar brand name
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not rename.');
    } finally {
      setBusy(false);
    }
  }

  async function saveCountry() {
    const entry = COUNTRIES.find((c) => c.iso === countryVal);
    if (!entry) { toast.error('Pick a country'); return; }
    setBusy(true);
    try {
      await api.patch('/settings/country', { countryCode: entry.iso, currency: entry.currency });
      toast.success('Country updated.');
      setEditingCountry(false);
      void refresh();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update country.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <>
        <PageHeader title="Settings" />
        <div className="page"><Banner kind="danger">{loadError}</Banner></div>
      </>
    );
  }

  if (!settings) {
    return (
      <>
        <PageHeader title="Settings" />
        <div className="page"><PageLoading rows={3} /></div>
      </>
    );
  }

  const isService = settings.businessModel === 'service';

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="How your company works, and what that means for the app."
      />

      <div className="page page-narrow">
        {!isAdmin ? (
          <div style={{ marginBottom: 16 }}>
            <Banner kind="info">
              You can see how your company is set up, but only an admin can change it.
            </Banner>
          </div>
        ) : null}

        <LogoCard
          logoUrl={company?.logoUrl ?? null}
          canEdit={isAdmin}
          onChanged={() => void refresh()}
        />

        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-head">
            <div>
              <h3>Company name</h3>
              <p className="small faint">The name shown in the sidebar and reports.</p>
            </div>
          </div>
          <div className="card-pad">
            {editingName ? (
              <div className="row gap-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  className="input" type="text" value={nameVal}
                  onChange={(e) => setNameVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  style={{ flex: 1, maxWidth: 320 }} autoFocus
                />
                <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void saveName()}>Save</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingName(false)}>Cancel</button>
              </div>
            ) : (
              <div className="row gap-8" style={{ alignItems: 'center' }}>
                <span style={{ fontWeight: 600 }}>{company?.name ?? '—'}</span>
                {isAdmin ? (
                  <button
                    type="button" className="btn btn-secondary btn-sm"
                    onClick={() => { setNameVal(company?.name ?? ''); setEditingName(true); }}
                  >
                    Rename
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <CountryCard
          countryCode={company?.countryCode ?? 'IN'}
          currency={company?.currency ?? 'INR'}
          isAdmin={isAdmin}
          busy={busy}
          editing={editingCountry}
          countryVal={countryVal}
          onEdit={() => { setCountryVal(company?.countryCode ?? 'IN'); setEditingCountry(true); }}
          onCancel={() => setEditingCountry(false)}
          onChange={setCountryVal}
          onSave={() => void saveCountry()}
        />

        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-head">
            <div>
              <h3>Business type</h3>
              <p className="small faint">
                How your team records a visit. This is set by Salestrendz — contact
                us if it needs to change.
              </p>
            </div>
          </div>
          <div className="card-pad">
            <div className="row gap-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="badge badge-brand">
                {isService ? 'Service / Field work' : 'Goods / Distribution'}
              </span>
              <span className="small faint">
                {isService
                  ? 'A visit is a place and a time — plotted on the map.'
                  : 'A visit is against a customer — a shop on the beat.'}
              </span>
            </div>
          </div>
        </div>

        <AttendanceCard settings={settings} isAdmin={isAdmin} onSave={save} />

        <div className="card">
          <div className="card-head"><h3>Customers</h3></div>
          <div className="card-pad col gap-16">
            <div className="col gap-4">
              <Toggle
                testId="reps-can-add"
                checked={settings.repsCanAddCustomers}
                disabled={!isAdmin || isService}
                label="Let salesmen add customers from the app"
                onChange={(repsCanAddCustomers) => void save(
                  { repsCanAddCustomers },
                  repsCanAddCustomers
                    ? 'Your team can add customers from the app.'
                    : 'Customers are now added from this console only.',
                )}
              />
              <span className="small faint" style={{ paddingLeft: 45 }}>
                {isService
                  ? 'Always on for a service business — there is no fixed list to pick from.'
                  : 'Turn this off if every customer is onboarded here with credit terms and a code.'}
              </span>
            </div>

            <div className="col gap-4">
              <Toggle
                testId="location-required-mobile"
                checked={settings.customerLocationRequiredMobile}
                disabled={!isAdmin || isService}
                label="Require a location on mobile"
                onChange={(customerLocationRequiredMobile) => void save(
                  { customerLocationRequiredMobile },
                  customerLocationRequiredMobile
                    ? 'Mobile app now requires a location on every new customer.'
                    : 'Mobile app no longer requires a location.',
                )}
              />
              <span className="small faint" style={{ paddingLeft: 45 }}>
                {isService
                  ? 'Always on for a service business — a customer with no pin cannot be plotted.'
                  : 'Reps capture it standing outside the shop.'}
              </span>
            </div>

            <div className="col gap-4">
              <Toggle
                testId="location-required-web"
                checked={settings.customerLocationRequiredWeb}
                disabled={!isAdmin}
                label="Require a location on web"
                onChange={(customerLocationRequiredWeb) => void save(
                  { customerLocationRequiredWeb },
                  customerLocationRequiredWeb
                    ? 'Web console now requires a location on every new customer.'
                    : 'Web console no longer requires a location.',
                )}
              />
              <span className="small faint" style={{ paddingLeft: 45 }}>
                Turn this off if the console sometimes adds shops without coordinates (bulk import, data cleanup).
              </span>
            </div>
          </div>
        </div>

        <BeatLevelsCard
          levels={settings.beatLevelNames ?? ['Area']}
          isAdmin={isAdmin}
          onSave={(beatLevelNames) => void save({ beatLevelNames }, 'Area hierarchy levels saved.', { optimistic: false })}
        />

        <div className="card">
          <div className="card-head"><h3>Visits</h3></div>
          <div className="card-pad col gap-16">
            <div className="col gap-4">
              <Toggle
                testId="journey-plan"
                checked={settings.journeyPlanEnabled}
                disabled={!isAdmin || isService}
                label="Use journey plans"
                onChange={(journeyPlanEnabled) => void save(
                  { journeyPlanEnabled },
                  journeyPlanEnabled
                    ? 'Reps now see the shops you planned for each weekday.'
                    : 'Reps now see every shop in the areas they cover.',
                )}
              />
              <span className="small faint" style={{ paddingLeft: 45 }}>
                {isService
                  ? 'Not available for a service business — your team goes where the jobs '
                    + 'are, so there is no fixed list to plan against.'
                  : 'Off, a rep\'s beat is every shop in their areas — no upkeep. On, you '
                    + 'set which shops each rep calls on each weekday, and coverage is '
                    + 'measured against that. Off-plan calls are still allowed either way.'}
              </span>
            </div>

            <div className="col gap-4">
              <Toggle
                testId="visit-selfie"
                checked={settings.visitSelfieRequired}
                disabled={!isAdmin}
                label="Take a selfie at check-in"
                onChange={(visitSelfieRequired) => void save(
                  { visitSelfieRequired },
                  visitSelfieRequired
                    ? 'Reps now take a selfie when they check in.'
                    : 'Check-in no longer asks for a selfie.',
                )}
              />
              <span className="small faint" style={{ paddingLeft: 45 }}>
                The strongest proof that the right person was at the shop. It reuses the
                face check from attendance, with no blink-and-turn challenge — that is
                fine twice a day, but not at twenty-five shops.
              </span>
            </div>

            <div className="col gap-4">
              <Toggle
                testId="visit-signature"
                checked={settings.visitSignatureRequired}
                disabled={!isAdmin}
                label="Get a signature at the end of a visit"
                onChange={(visitSignatureRequired) => void save(
                  { visitSignatureRequired },
                  visitSignatureRequired
                    ? 'Visits now end with a signature.'
                    : 'Visits no longer ask for a signature.',
                )}
              />
              <span className="small faint" style={{ paddingLeft: 45 }}>
                Standard in field service. &ldquo;Nobody available to sign&rdquo; stays a
                real answer — a job finished in an empty flat must not trap the rep on
                that screen.
              </span>
            </div>

            <div className="col gap-4">
              <label className="small" style={{ fontWeight: 600 }}>
                Flag a visit recorded more than this far from the shop
              </label>
              <div className="row gap-8">
                <input
                  type="number"
                  className="input"
                  style={{ maxWidth: 120 }}
                  min={25}
                  max={20000}
                  step={25}
                  disabled={!isAdmin || busy}
                  value={settings.visitFarThresholdM}
                  onChange={(e) => setSettings({
                    ...settings, visitFarThresholdM: Number(e.target.value),
                  })}
                  onBlur={(e) => void save(
                    { visitFarThresholdM: Number(e.target.value) },
                    'Saved.',
                    { optimistic: false },
                  )}
                />
                <span className="small faint">metres</span>
              </div>
              <span className="small faint">
                Nothing is blocked at this distance. A shop&rsquo;s stored pin is often the
                thing that is wrong, and turning away a rep who is standing in the right
                place is the worse mistake — so the distance is recorded, the visit is
                flagged for you to look at, and the rep is offered the chance to move the
                pin to where they are.
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>Orders</h3></div>
          <div className="card-pad col gap-16">
            <div className="col gap-4">
              <Toggle
                testId="orders-enabled"
                checked={settings.ordersEnabled}
                disabled={!isAdmin}
                label="Let your team book orders"
                onChange={(ordersEnabled) => void save(
                  { ordersEnabled },
                  ordersEnabled
                    ? 'Order booking is on. Reps see it on their next sync.'
                    : 'Order booking is off. Existing orders are kept.',
                )}
              />
              <span className="small faint" style={{ paddingLeft: 45 }}>
                Off, there is no order button on the phone and no Orders section here.
                Turn it on once your products have prices — a rep who opens an empty
                catalogue in front of a shopkeeper is worse off than one with no button
                at all. Switching it back off hides the module; nothing already booked
                is lost.
              </span>
            </div>

            {settings.ordersEnabled ? (
              <>
                <div className="col gap-4">
                  <label className="small" style={{ fontWeight: 600 }}>
                    How much a rep may discount a line
                  </label>
                  <div className="row gap-8">
                    <input
                      type="number"
                      className="input"
                      style={{ maxWidth: 110 }}
                      min={0}
                      max={100}
                      step={0.5}
                      disabled={!isAdmin || busy}
                      value={settings.orderMaxDiscountPercent}
                      onChange={(e) => setSettings({
                        ...settings, orderMaxDiscountPercent: Number(e.target.value),
                      })}
                      onBlur={(e) => void save(
                        { orderMaxDiscountPercent: Number(e.target.value) },
                        'Saved.',
                        { optimistic: false },
                      )}
                    />
                    <span className="small faint">percent</span>
                  </div>
                  <span className="small faint">
                    Zero means no discounting at all, which is the default: a rep who can
                    knock money off without a ceiling makes your price list advisory. The
                    server enforces this — the app cannot send its own price.
                  </span>
                </div>

                <div className="col gap-4">
                  <label className="small" style={{ fontWeight: 600 }}>
                    Order number prefix
                  </label>
                  <div className="row gap-8">
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: 110 }}
                      maxLength={8}
                      disabled={!isAdmin || busy}
                      value={settings.orderNumberPrefix}
                      onChange={(e) => setSettings({
                        ...settings, orderNumberPrefix: e.target.value.toUpperCase(),
                      })}
                      onBlur={(e) => void save(
                        { orderNumberPrefix: e.target.value.trim().toUpperCase() },
                        'Saved.',
                        { optimistic: false },
                      )}
                    />
                    <span className="small faint">
                      Next order will be {settings.orderNumberPrefix || 'SO'}-00001
                    </span>
                  </div>
                  <span className="small faint">
                    Numbers are sequential and never reused. Changing the prefix does not
                    renumber anything already booked.
                  </span>
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>Expenses</h3></div>
          <div className="card-pad col gap-16">
            <div className="col gap-4">
              <label className="small" style={{ fontWeight: 600 }}>How claims are approved</label>
              <select
                className="input"
                style={{ maxWidth: 320 }}
                disabled={!isAdmin || busy}
                value={settings.expenseApprovalMode}
                onChange={(e) => void save(
                  { expenseApprovalMode: e.target.value as CompanySettings['expenseApprovalMode'] },
                  'Approval flow saved.',
                  { optimistic: false },
                )}
              >
                <option value="two_level">Salesman → Manager → Admin</option>
                <option value="admin_only">Salesman / Manager → Admin</option>
              </select>
              <span className="small faint">
                {settings.expenseApprovalMode === 'two_level'
                  ? 'A salesman’s claim goes to their reporting manager first, then to an admin for final approval, then it is disbursed. A manager’s own claim (or a rep with no manager) goes straight to an admin.'
                  : 'Every claim goes straight to an admin to approve, then it is disbursed. Managers do not approve.'}
                {' '}Either way, an admin marks an approved claim as disbursed. Set the allowance rates under Expenses → Categories &amp; rates.
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>Live tracking</h3></div>
          <div className="card-pad col gap-16">
            <div className="col gap-4">
              <Toggle
                testId="tracking-enabled"
                checked={settings.trackingEnabled}
                disabled={!isAdmin}
                label="Track reps on the map during their shift"
                onChange={(trackingEnabled) => void save(
                  { trackingEnabled },
                  trackingEnabled
                    ? 'Tracking is on. Reps are located between check-in and check-out.'
                    : 'Tracking is off. No new location is recorded.',
                )}
              />
              <span className="small faint" style={{ paddingLeft: 45 }}>
                Location is recorded only between attendance check-in and check-out —
                never off-shift. The team map and route replay appear here once this is
                on. Reps see a persistent notice on their phone while it runs.
              </span>
            </div>

            {settings.trackingEnabled ? (
              <>
                <div className="col gap-4">
                  <label className="small" style={{ fontWeight: 600 }}>
                    Count a pause as a stop after
                  </label>
                  <div className="row gap-8">
                    <input
                      type="number"
                      className="input"
                      style={{ maxWidth: 110 }}
                      min={2}
                      max={120}
                      step={1}
                      disabled={!isAdmin || busy}
                      value={settings.trackingStopMinMinutes}
                      onChange={(e) => setSettings({
                        ...settings, trackingStopMinMinutes: Number(e.target.value),
                      })}
                      onBlur={(e) => void save(
                        { trackingStopMinMinutes: Number(e.target.value) },
                        'Saved.',
                        { optimistic: false },
                      )}
                    />
                    <span className="small faint">minutes in one place</span>
                  </div>
                  <span className="small faint">
                    A rep who stays inside a small radius for this long is marked as
                    stopped there. If a shop is nearby the stop is matched to it; if not,
                    it is flagged as an unlogged stop for you to look at.
                  </span>
                </div>

                <div className="col gap-4">
                  <label className="small" style={{ fontWeight: 600 }}>
                    Treat a silence as lost signal after
                  </label>
                  <div className="row gap-8">
                    <input
                      type="number"
                      className="input"
                      style={{ maxWidth: 110 }}
                      min={2}
                      max={120}
                      step={1}
                      disabled={!isAdmin || busy}
                      value={settings.trackingGapMinutes}
                      onChange={(e) => setSettings({
                        ...settings, trackingGapMinutes: Number(e.target.value),
                      })}
                      onBlur={(e) => void save(
                        { trackingGapMinutes: Number(e.target.value) },
                        'Saved.',
                        { optimistic: false },
                      )}
                    />
                    <span className="small faint">minutes with no ping</span>
                  </div>
                  <span className="small faint">
                    A gap this long shows on the route as a dashed line rather than a
                    straight one drawn through places the rep never went. Usually a
                    tunnel, a dead battery, or the app being force-stopped.
                  </span>
                </div>
              </>
            ) : null}
          </div>
        </div>

        <MobileMenuCard settings={settings} isAdmin={isAdmin} busy={busy} onSave={save} />

        <p className="small faint" style={{ marginTop: 16 }}>
          Changes reach your team the next time their app syncs.
        </p>
      </div>

    </>
  );
}

/**
 * The mobile bottom menu. The admin picks which modules appear in the
 * app's bottom bar, their order, and an optional label for each. "More"
 * is always added by the app and holds whatever is not placed here.
 */
const MENU_CATALOGUE: { key: MobileMenuKey; label: string; needs?: keyof CompanySettings }[] = [
  { key: 'attendance', label: 'Attendance' },
  { key: 'visits', label: 'Visits' },
  { key: 'orders', label: 'Orders', needs: 'ordersEnabled' },
  { key: 'customers', label: 'Customers', needs: 'customersEnabled' },
  { key: 'leads', label: 'Leads', needs: 'leadsEnabled' },
  { key: 'expenses', label: 'Expenses' },
];

const BOTTOM_BAR_CAP = 4;

/** Every module enabled for this company, in the canonical default order. */
function enabledKeys(settings: CompanySettings): MobileMenuKey[] {
  return MENU_CATALOGUE
    .filter((c) => !c.needs || settings[c.needs] !== false)
    .map((c) => c.key);
}

/**
 * The ordered menu for the card: the company's saved order (keeping only
 * enabled modules), then any enabled module it left out appended to the
 * end — so every module always has a home, matching the app.
 */
function resolvedItems(settings: CompanySettings): MobileMenuItem[] {
  const enabled = new Set(enabledKeys(settings));
  const out: MobileMenuItem[] = [];
  const seen = new Set<MobileMenuKey>();
  for (const it of settings.mobileMenu ?? []) {
    if (enabled.has(it.key) && !seen.has(it.key)) { out.push(it); seen.add(it.key); }
  }
  for (const key of enabledKeys(settings)) {
    if (!seen.has(key)) { out.push({ key }); seen.add(key); }
  }
  return out;
}


// ---------------------------------------------------------------------------
// Country card — sets currency and calling code together
// ---------------------------------------------------------------------------

function CountryCard({ countryCode, currency, isAdmin, busy, editing, countryVal, onEdit, onCancel, onChange, onSave }: {
  countryCode: string; currency: string; isAdmin: boolean; busy: boolean;
  editing: boolean; countryVal: string;
  onEdit: () => void; onCancel: () => void;
  onChange: (iso: string) => void; onSave: () => void;
}) {
  const current = COUNTRIES.find((c) => c.iso === countryCode);
  const selected = COUNTRIES.find((c) => c.iso === countryVal);

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-head">
        <div>
          <h3>Country</h3>
          <p className="small faint">Sets the default currency and calling code for your team.</p>
        </div>
      </div>
      <div className="card-pad">
        {editing ? (
          <div className="col gap-8">
            <div className="row gap-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                className="input"
                value={countryVal}
                onChange={(e) => onChange(e.target.value)}
                style={{ maxWidth: 280 }}
                autoFocus
              >
                {COUNTRIES.map((c) => (
                  <option key={c.iso} value={c.iso}>{c.name} ({c.iso})</option>
                ))}
              </select>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={onSave}>Save</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
            </div>
            {selected ? (
              <span className="small faint">
                Currency: <strong>{selected.currency}</strong> &middot; Calling code: <strong>{selected.dial}</strong>
              </span>
            ) : null}
          </div>
        ) : (
          <div className="col gap-4">
            <div className="row gap-8" style={{ alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>{current?.name ?? countryCode}</span>
              {isAdmin ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={onEdit}>
                  Change
                </button>
              ) : null}
            </div>
            <span className="small faint">
              Currency: <strong>{currency}</strong>
              {current ? <> &middot; Calling code: <strong>{current.dial}</strong></> : null}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Beat hierarchy levels card
// ---------------------------------------------------------------------------

function BeatLevelsCard({
  levels, isAdmin, onSave,
}: { levels: string[]; isAdmin: boolean; onSave: (names: string[]) => void }) {
  const [editing, setEditing] = useState(false);
  const [names, setNames] = useState<string[]>(levels);

  // Sync when parent settings reload
  useEffect(() => { setNames(levels); }, [levels]);

  function updateName(i: number, val: string) {
    setNames((prev) => prev.map((n, j) => (j === i ? val : n)));
  }
  function addLevel() {
    setNames((prev) => [...prev, '']);
  }
  function removeLevel(i: number) {
    setNames((prev) => prev.filter((_, j) => j !== i));
  }

  function handleSave() {
    const trimmed = names.map((n) => n.trim()).filter(Boolean);
    if (!trimmed.length) return;
    onSave(trimmed);
    setEditing(false);
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Areas / Routes hierarchy</h3>
        {isAdmin && !editing && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
            <Icon name="edit" size={14} /> Configure levels
          </button>
        )}
      </div>
      <div className="card-pad col gap-12">
        {!editing ? (
          <div>
            <p className="small faint" style={{ marginBottom: 8 }}>
              {levels.length === 1
                ? 'Your areas are flat — one level.'
                : `${levels.length} levels: ${levels.join(' \u2192 ')}`}
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {levels.map((lvl, i) => (
                <span
                  key={i}
                  style={{
                    padding: '4px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                    background: 'var(--surface-2, #f3f4f6)', color: 'var(--ink-muted, #52565e)',
                  }}
                >
                  {i + 1}. {lvl}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="col gap-8">
            <p className="small faint">
              Name each level from broadest to most specific. The final level is the one assigned to salespeople.
            </p>
            {names.map((n, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="faint" style={{ width: 24, textAlign: 'center', fontSize: 13, fontWeight: 600 }}>{i + 1}</span>
                <input
                  type="text"
                  className="input"
                  value={n}
                  placeholder={`Level ${i + 1} name`}
                  maxLength={40}
                  style={{ flex: 1 }}
                  autoFocus={i === names.length - 1}
                  onChange={(e) => updateName(i, e.target.value)}
                />
                {names.length > 1 && (
                  <button
                    type="button"
                    className="icon-btn danger"
                    title="Remove level"
                    onClick={() => removeLevel(i)}
                  >
                    <Icon name="trash" size={14} />
                  </button>
                )}
              </div>
            ))}
            {names.length < 5 && (
              <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={addLevel}>
                <Icon name="plus" size={14} /> Add level
              </button>
            )}
            <div className="row gap-8" style={{ marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => { setNames(levels); setEditing(false); }}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={names.every((n) => !n.trim()) || names.length === 0}
                onClick={handleSave}
              >
                Save levels
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileMenuCard({ settings, isAdmin, busy, onSave }: {
  settings: CompanySettings;
  isAdmin: boolean;
  busy: boolean;
  onSave: (patch: Partial<CompanySettings>, success: string, opts?: { optimistic?: boolean }) => void;
}) {
  const [items, setItems] = useState<MobileMenuItem[]>(resolvedItems(settings));
  const [dirty, setDirty] = useState(false);

  // Keep local state in step if the saved value changes elsewhere.
  useEffect(() => {
    if (!dirty) setItems(resolvedItems(settings));
  }, [settings.mobileMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  const labelFor = (key: MobileMenuKey) => MENU_CATALOGUE.find((c) => c.key === key)?.label ?? key;

  const update = (next: MobileMenuItem[]) => { setItems(next); setDirty(true); };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j]!, next[i]!];
    update(next);
  };
  const relabel = (i: number, label: string) =>
    update(items.map((it, idx) => (idx === i ? { ...it, label: label || undefined } : it)));

  return (
    <div className="card">
      <div className="card-head"><h3>Mobile app menu</h3></div>
      <div className="card-pad col gap-16">
        <span className="small faint">
          Order the modules for the app. The first {BOTTOM_BAR_CAP} are the bottom bar; the rest
          sit under the “More” tab in this order. Use the arrows to move items up or down, and
          leave a label blank to use the default name.
        </span>

        <div className="col gap-4">
          {items.map((it, i) => (
            <div key={it.key}>
              {i === BOTTOM_BAR_CAP ? (
                <div className="row gap-8" style={{ alignItems: 'center', margin: '6px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span className="small faint" style={{ whiteSpace: 'nowrap' }}>
                    ↓ below here shows under “More”
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
              ) : null}
              <div className="row gap-8" style={{ alignItems: 'center' }}>
                <span className="small faint" style={{ width: 20, textAlign: 'right' }}>
                  {i < BOTTOM_BAR_CAP ? i + 1 : '•'}
                </span>
                <div className="row gap-4">
                  <button type="button" className="icon-btn" disabled={!isAdmin || i === 0} title="Up"
                    onClick={() => move(i, -1)}><Icon name="chevron-down" size={14} className="nav-caret open" /></button>
                  <button type="button" className="icon-btn" disabled={!isAdmin || i === items.length - 1} title="Down"
                    onClick={() => move(i, 1)}><Icon name="chevron-down" size={14} /></button>
                </div>
                <strong className="small" style={{ width: 90 }}>{labelFor(it.key)}</strong>
                <input className="input" style={{ flex: 1 }} disabled={!isAdmin}
                  placeholder={`Label (default: ${labelFor(it.key)})`}
                  value={it.label ?? ''} onChange={(e) => relabel(i, e.target.value)} />
              </div>
            </div>
          ))}
        </div>

        {isAdmin ? (
          <div className="row gap-8">
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || !dirty}
              onClick={() => { onSave({ mobileMenu: items }, 'Mobile menu saved.', { optimistic: false }); setDirty(false); }}>
              Save menu
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
              onClick={() => { onSave({ mobileMenu: null }, 'Reset to the default menu.', { optimistic: false }); setDirty(false); }}>
              Reset to default
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Auto check-out.
 *
 * A rep who forgets to check out is closed automatically. One default time
 * covers every day; a per-day override handles the odd short Saturday. A
 * day with neither is closed when the day changes (midnight). Times are
 * HH:MM in the company's timezone.
 */
const WEEKDAYS: [string, string][] = [
  ['1', 'Monday'], ['2', 'Tuesday'], ['3', 'Wednesday'], ['4', 'Thursday'],
  ['5', 'Friday'], ['6', 'Saturday'], ['7', 'Sunday'],
];

function AttendanceCard({ settings, isAdmin, onSave }: {
  settings: CompanySettings;
  isAdmin: boolean;
  onSave: (patch: Partial<CompanySettings>, success: string, opts?: { optimistic?: boolean }) => void;
}) {
  const byWeekday = settings.autoCheckoutByWeekday ?? {};
  const toast = useToast();

  // Face check-in lives in the attendance module's own settings, not the
  // company settings this card otherwise edits, so it loads separately.
  const [face, setFace] = useState<{ requireFace: boolean; requireLiveness: boolean } | null>(null);
  const [faceBusy, setFaceBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get<{ requireFace: boolean; requireLiveness: boolean }>('/attendance/settings')
      .then((s) => { if (!cancelled) setFace({ requireFace: s.requireFace, requireLiveness: s.requireLiveness }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  async function saveFace(patch: Partial<{ requireFace: boolean; requireLiveness: boolean }>, success: string) {
    const previous = face;
    if (previous) setFace({ ...previous, ...patch });
    setFaceBusy(true);
    try {
      const next = await api.patch<{ requireFace: boolean; requireLiveness: boolean }>('/attendance/settings', patch);
      setFace({ requireFace: next.requireFace, requireLiveness: next.requireLiveness });
      toast.success(success);
    } catch (err) {
      setFace(previous);
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save that.');
    } finally {
      setFaceBusy(false);
    }
  }

  const setDefault = (v: string) =>
    onSave({ autoCheckoutDefault: v || null }, 'Auto check-out saved.');

  const setDay = (key: string, v: string) => {
    const next: Record<string, string> = { ...byWeekday };
    if (v) next[key] = v; else delete next[key];
    onSave({ autoCheckoutByWeekday: next }, 'Auto check-out saved.');
  };

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Attendance</h3>
          <p className="small faint">
            Your working week and when an open shift is closed automatically.
          </p>
        </div>
      </div>
      <div className="card-pad col gap-16">
        {face ? (
          <div className="col gap-4">
            <Toggle
              testId="attendance-face"
              checked={face.requireFace}
              disabled={!isAdmin || faceBusy}
              label="Face check at attendance"
              onChange={(requireFace) => void saveFace(
                { requireFace },
                requireFace
                  ? 'Face check is on. Reps set up their face once, then it is checked at every punch.'
                  : 'Face check is off. Reps punch in and out without a photo.',
              )}
            />
            <span className="small faint" style={{ paddingLeft: 45 }}>
              When on, each rep takes a one-time setup photo and their face is matched every time
              they punch in or out. Turn it off and the app goes straight to the punch button.
            </span>
          </div>
        ) : null}

        {face?.requireFace ? (
          <div className="col gap-4">
            <Toggle
              testId="attendance-liveness"
              checked={face.requireLiveness}
              disabled={!isAdmin || faceBusy}
              label="Blink-and-turn check with the face photo"
              onChange={(requireLiveness) => void saveFace(
                { requireLiveness },
                requireLiveness
                  ? 'Liveness check is on.'
                  : 'Liveness check is off. The face photo alone is used.',
              )}
            />
            <span className="small faint" style={{ paddingLeft: 45 }}>
              Asks the rep to blink and turn their head, so a photo of a photo cannot be used.
            </span>
          </div>
        ) : null}

        <div className="col gap-4">
          <Toggle
            checked={settings.speedometerEnabled}
            disabled={!isAdmin}
            label="Speedometer on check-in"
            onChange={(speedometerEnabled) => onSave(
              { speedometerEnabled },
              speedometerEnabled
                ? 'Speedometer is on. Reps must photograph the odometer at check-in and check-out.'
                : 'Speedometer is off.',
            )}
          />
          <span className="small faint" style={{ paddingLeft: 45 }}>
            When on, a rep photographs the vehicle odometer at check-in and check-out and the
            reading is scanned from the photo. The day&apos;s distance is the difference between
            the two, and it is offered alongside the GPS distance on a Travel Allowance claim.
          </span>
        </div>

        <div className="col gap-4">
          <label className="small" style={{ fontWeight: 600 }}>Working days</label>
          <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
            {WEEKDAYS.map(([key, label]) => {
              const on = (settings.workingWeekdays ?? []).includes(Number(key));
              return (
                <button
                  key={key}
                  type="button"
                  disabled={!isAdmin}
                  className={`day-toggle${on ? ' on' : ''}`}
                  onClick={() => {
                    const set = new Set(settings.workingWeekdays ?? []);
                    if (set.has(Number(key))) set.delete(Number(key)); else set.add(Number(key));
                    const next = [...set].sort((a, b) => a - b);
                    if (next.length === 0) return; // a company works at least one day
                    onSave({ workingWeekdays: next }, 'Working days saved.');
                  }}
                >
                  {label.slice(0, 3)}
                </button>
              );
            })}
          </div>
          <span className="small faint">
            {(settings.workingWeekdays ?? []).length} working day
            {(settings.workingWeekdays ?? []).length === 1 ? '' : 's'} a week. A non-working
            day is a weekly off — it does not count as absent.
          </span>
        </div>

        <div className="col gap-4">
          <label className="small" style={{ fontWeight: 600 }}>Auto check-out — default time (all days)</label>
          <div className="row gap-8" style={{ alignItems: 'center' }}>
            <input
              type="time"
              className="input"
              style={{ maxWidth: 150 }}
              disabled={!isAdmin}
              value={settings.autoCheckoutDefault ?? ''}
              onChange={(e) => setDefault(e.target.value)}
            />
            {settings.autoCheckoutDefault ? (
              <button type="button" className="btn btn-ghost btn-sm" disabled={!isAdmin}
                onClick={() => setDefault('')}>Clear</button>
            ) : null}
          </div>
          <span className="small faint">
            Leave blank to close every day at the day change, unless a day below sets its own time.
          </span>
        </div>

        <div className="col gap-4">
          <label className="small" style={{ fontWeight: 600 }}>Per-day override</label>
          <div className="col gap-8" style={{ marginTop: 2 }}>
            {WEEKDAYS.map(([key, label]) => (
              <div key={key} className="row gap-8" style={{ alignItems: 'center' }}>
                <span className="small" style={{ width: 82 }}>{label}</span>
                <input
                  type="time"
                  className="input"
                  style={{ maxWidth: 150 }}
                  disabled={!isAdmin}
                  value={byWeekday[key] ?? ''}
                  onChange={(e) => setDay(key, e.target.value)}
                />
                <span className="small faint">
                  {byWeekday[key]
                    ? ''
                    : settings.autoCheckoutDefault
                      ? `Uses default (${settings.autoCheckoutDefault})`
                      : 'Closes at day change'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Company logo. Shown on the console and in the app; uploaded here by an
 * admin. The file is read to a data URL and posted; the server stores it
 * and returns a fresh URL. On success we refresh the auth context so the
 * new logo appears in the sidebar immediately, not on next login.
 */
function LogoCard({
  logoUrl, canEdit, onChanged,
}: {
  logoUrl: string | null;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function onFile(file: File) {
    // ~2 MB is plenty for a logo; anything larger is a photo by mistake.
    if (file.size > 2_000_000) {
      toast.error('That image is over 2 MB. Please use a smaller logo.');
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      toast.error('Use a PNG, JPG or WebP image.');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      await api.post('/settings/logo', { image: dataUrl });
      toast.success('Logo updated');
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not upload that.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.del('/settings/logo');
      toast.success('Logo removed');
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove it.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-head"><h3>Company logo</h3></div>
      <div className="card-pad">
        <div className="logo-setting">
          <div className="logo-preview">
            {logoUrl
              ? <img src={logoUrl} alt="Company logo" />
              : <span className="faint small">No logo</span>}
          </div>
          <div className="col gap-8" style={{ flex: 1, minWidth: 0 }}>
            <p className="small faint" style={{ margin: 0 }}>
              Shown on this dashboard and in your team&apos;s app. A square PNG with a
              transparent background works best. Up to 2&nbsp;MB.
            </p>
            {canEdit ? (
              <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
                <label className={`btn btn-secondary btn-sm${busy ? ' disabled' : ''}`}>
                  <Icon name="plus" size={15} />
                  {logoUrl ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: 'none' }}
                    disabled={busy}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void onFile(f);
                      e.target.value = '';
                    }}
                  />
                </label>
                {logoUrl ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={busy}
                    onClick={() => void remove()}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ) : (
              <p className="small faint" style={{ margin: 0 }}>Only an admin can change this.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
